/**
 * Report Block View
 *
 * Renders an assessment report with move controls on each block.
 * Parses the monolithic HTML into sections and blocks, then renders
 * each block as a React-wrapped element with hover-activated move buttons.
 */

import { useMemo, useState, useCallback } from 'react';
import { type SectionType, type ParsedBlock, parseReportForBlockView, getValidTargetSections } from './reportHtmlUtils';
import { BlockMoveModal } from './BlockMoveModal';

interface ReportBlockViewProps {
  html: string;
  onMoveBlock: (fromSection: SectionType, fromIndex: number, toSection: SectionType, toIndex: number) => void;
  onDeleteBlock: (sectionType: SectionType, blockIndex: number) => void;
  isAiChatOpen: boolean;
}

interface ActiveMoveState {
  sectionType: SectionType;
  blockIndex: number;
  blockTitle: string;
  anchorRect: DOMRect | null;
}

interface PendingDeleteState {
  sectionType: SectionType;
  blockIndex: number;
  blockTitle: string;
}

const MOVE_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="5 9 2 12 5 15" />
    <polyline points="9 5 12 2 15 5" />
    <polyline points="15 19 12 22 9 19" />
    <polyline points="19 9 22 12 19 15" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <line x1="12" y1="2" x2="12" y2="22" />
  </svg>
);

const DELETE_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

/** Inline styles for block action buttons and hover behavior */
const BLOCK_VIEW_STYLES = `
  .block-actions {
    opacity: 0;
    transition: opacity 0.15s;
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 4px;
    z-index: 10;
  }
  .block-wrapper:hover .block-actions {
    opacity: 1;
  }
  .block-action-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    cursor: pointer;
    color: #6b7280;
    padding: 0;
  }
  .block-action-btn:hover {
    background: #f3f4f6;
    color: #374151;
    border-color: #9ca3af;
  }
  .block-delete-btn:hover {
    background: #fef2f2;
    color: #dc2626;
    border-color: #fca5a5;
  }
  @media print {
    .block-actions { display: none !important; }
  }
`;

export function ReportBlockView({ html, onMoveBlock, onDeleteBlock, isAiChatOpen }: ReportBlockViewProps) {
  const parsed = useMemo(() => parseReportForBlockView(html), [html]);
  const [activeMove, setActiveMove] = useState<ActiveMoveState | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDeleteState | null>(null);

  const handleDeleteClick = useCallback((
    sectionType: SectionType,
    blockIndex: number,
    blockTitle: string,
  ) => {
    setPendingDelete({ sectionType, blockIndex, blockTitle });
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!pendingDelete) return;
    onDeleteBlock(pendingDelete.sectionType, pendingDelete.blockIndex);
    setPendingDelete(null);
  }, [pendingDelete, onDeleteBlock]);

  const handleDeleteCancel = useCallback(() => {
    setPendingDelete(null);
  }, []);

  const handleMoveClick = useCallback((
    sectionType: SectionType,
    blockIndex: number,
    blockTitle: string,
    event: React.MouseEvent,
  ) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    setActiveMove({ sectionType, blockIndex, blockTitle, anchorRect: rect });
  }, []);

  const handleMoveConfirm = useCallback((toSection: SectionType, toIndex: number) => {
    if (!activeMove) return;
    onMoveBlock(activeMove.sectionType, activeMove.blockIndex, toSection, toIndex);
    setActiveMove(null);
  }, [activeMove, onMoveBlock]);

  const handleMoveClose = useCallback(() => {
    setActiveMove(null);
  }, []);

  // Fallback: if HTML can't be parsed, render as plain HTML
  if (!parsed) {
    return (
      <div
        dangerouslySetInnerHTML={{ __html: html }}
        className={`assessment-report-content transition-all duration-300 ${isAiChatOpen ? 'mr-[400px]' : ''}`}
      />
    );
  }

  // Build sectionBlocks lookup for the modal
  const sectionBlocks: Record<SectionType, ParsedBlock[]> = {
    positive: [],
    improvement: [],
    agreements: [],
    conclusion: [],
  };
  for (const section of parsed.sections) {
    sectionBlocks[section.sectionType] = section.blocks;
  }

  return (
    <>
      {/* Inject report CSS + block move styles */}
      <style dangerouslySetInnerHTML={{ __html: parsed.css + '\n' + BLOCK_VIEW_STYLES }} />

      <div className={`assessment-report-content transition-all duration-300 ${isAiChatOpen ? 'mr-[400px]' : ''}`}>
        <div className="report-container">
          {/* Header and employee info - rendered as-is */}
          <div dangerouslySetInnerHTML={{ __html: parsed.headerHtml }} />
          <div dangerouslySetInnerHTML={{ __html: parsed.employeeInfoHtml }} />

          <div className="content">
            {parsed.sections.map((section, sIdx) => {
              const validTargets = getValidTargetSections(section.sectionType);
              const hasMovableBlocks = validTargets.length > 0 && section.blocks.length > 0;
              // Show move buttons if cross-section possible, or more than 1 block to reorder
              const showMoveButtons = hasMovableBlocks && (
                validTargets.length > 1 || section.blocks.length > 1
              );

              return (
                <div key={sIdx} className="section">
                  <div dangerouslySetInnerHTML={{ __html: section.headerHtml }} />
                  <div dangerouslySetInnerHTML={{ __html: section.summaryHtml }} />

                  {section.sectionType === 'conclusion' ? (
                    // Conclusion: render conclusion-box, no move controls
                    section.conclusionBoxHtml ? (
                      <div dangerouslySetInnerHTML={{ __html: section.conclusionBoxHtml }} />
                    ) : null
                  ) : section.blocks.length > 0 ? (
                    <div className="items-list">
                      {section.blocks.map((block, bIdx) => (
                        <div
                          key={bIdx}
                          className="block-wrapper"
                          style={{ position: 'relative' }}
                        >
                          <div dangerouslySetInnerHTML={{ __html: block.html }} />
                          <div className="block-actions no-print">
                            <button
                              className="block-action-btn block-delete-btn"
                              onClick={() => handleDeleteClick(section.sectionType, bIdx, block.title)}
                              title="Verwijder dit blok"
                            >
                              {DELETE_ICON}
                            </button>
                            {showMoveButtons && (
                              <button
                                className="block-action-btn"
                                onClick={(e) => handleMoveClick(section.sectionType, bIdx, block.title, e)}
                                title="Verplaats dit blok"
                              >
                                {MOVE_ICON}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : section.noItemsHtml ? (
                    <div dangerouslySetInnerHTML={{ __html: section.noItemsHtml }} />
                  ) : null}
                </div>
              );
            })}

            {/* Signature - inside .content in the original HTML */}
            {parsed.signatureHtml && (
              <div dangerouslySetInnerHTML={{ __html: parsed.signatureHtml }} />
            )}
          </div>

          {/* Footer - inside .report-container but outside .content */}
          {parsed.footerHtml && (
            <div dangerouslySetInnerHTML={{ __html: parsed.footerHtml }} />
          )}
        </div>
      </div>

      {/* Move Modal */}
      {activeMove && (
        <BlockMoveModal
          blockTitle={activeMove.blockTitle}
          sourceSectionType={activeMove.sectionType}
          sourceIndex={activeMove.blockIndex}
          sectionBlocks={sectionBlocks}
          onMove={handleMoveConfirm}
          onClose={handleMoveClose}
          anchorRect={activeMove.anchorRect}
        />
      )}

      {/* Delete Confirmation Modal */}
      {pendingDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900">Blok verwijderen</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Weet je zeker dat je <strong>"{pendingDelete.blockTitle}"</strong> wilt verwijderen?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
